import { useTranslation } from "react-i18next";

import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import { usageProfileLabel } from "../acts/environment/usageProfileLabel";
import type { ActId } from "../acts";
import { NO_VALUE } from "../sectionState";
import { StackedBar } from "../primitives/StackedBar";
import { SummaryBlock, SummaryStat } from "./SummaryBlock";
import { SummaryLegend, Swatch } from "./SummaryLegend";
import type { DiagnosticSummary } from "./summaryData";

/** As cores dos quatro perfis, da mais escura (a que mais pesa) à mais clara. */
const PROFILE_COLOR = {
  operational: "bg-[#08254c]",
  self_service: "bg-[#4f6fb0]",
  technical: "bg-[#9fb3cf]",
  managerial: "bg-[#d4dae3]",
} as const;

/**
 * Bloco 1 · O ambiente (EF-DIAG-004 RF-03). Os números são os da visão geral do
 * Ato 1; a barra é a do perfil de uso, na ordem do card.
 */
export function EnvironmentBlock({
  summary,
  act,
  onGoToAct,
}: {
  summary: DiagnosticSummary["environment"];
  act: ActId;
  onGoToAct: (act: ActId) => void;
}) {
  const { t } = useTranslation();
  const { activeUsers, companies, scopeItems, usageProfile } = summary;
  const leader = usageProfile?.rows[0];

  /** A frase só existe com os números que ela cita; sem empresas, cai a cláusula. */
  function reading(): string | undefined {
    if (activeUsers === undefined || !leader || leader.share === undefined) return undefined;
    const vars = {
      users: formatCount(activeUsers),
      percent: `${Math.round(leader.share * 100)}%`,
      profile: usageProfileLabel(leader.bucket, t).toLocaleLowerCase(),
    };
    if (companies === undefined) {
      return t("{{users}} active users, {{percent}} in the {{profile}} profile.", vars);
    }
    return tp(
      t,
      companies,
      "{{users}} active users, {{percent}} in the {{profile}} profile, in {{count}} company.",
      "{{users}} active users, {{percent}} in the {{profile}} profile, in {{count}} companies.",
      vars,
    );
  }

  return (
    <SummaryBlock
      id="environment"
      index={1}
      overline={t("The environment")}
      title={t("What exists today")}
      act={act}
      onGoToAct={onGoToAct}
      reading={reading()}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryStat
          testId="summary-users"
          value={activeUsers === undefined ? NO_VALUE : formatCount(activeUsers)}
          label={t("Active users")}
        />
        <SummaryStat
          testId="summary-companies"
          value={companies === undefined ? NO_VALUE : formatCount(companies)}
          label={t("Companies")}
        />
        <SummaryStat
          testId="summary-scope-items"
          value={scopeItems === undefined ? NO_VALUE : formatCount(scopeItems.active)}
          label={t("Active scope items")}
          hint={
            scopeItems?.lobCount === undefined
              ? undefined
              : t("Lines of business: {{lobs}}", { lobs: formatCount(scopeItems.lobCount) })
          }
        />
      </div>

      {usageProfile && (
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {t("Active users by usage profile")}
          </div>
          <StackedBar
            height="h-3"
            segments={usageProfile.rows.map((row) => ({
              key: row.bucket,
              label: usageProfileLabel(row.bucket, t),
              className: PROFILE_COLOR[row.bucket],
              value: row.count,
            }))}
          />
          <SummaryLegend
            testIdPrefix="summary-profile"
            items={usageProfile.rows.map((row) => ({
              key: row.bucket,
              label: usageProfileLabel(row.bucket, t),
              value: formatCount(row.count),
              share: row.share === undefined ? undefined : `${Math.round(row.share * 100)}%`,
              swatch: <Swatch className={PROFILE_COLOR[row.bucket]} />,
            }))}
          />
        </div>
      )}
    </SummaryBlock>
  );
}
